import type { AbsenceDayPart } from './model';

export type AbsenceCalendarEntry = {
  startDate: string;
  endDate: string;
  dayPart?: AbsenceDayPart;
  quantity: string;
  unitOfTime: string;
  timeOffType: { id: string; descriptor?: string };
  displayColor?: string;
};

export type AbsenceCalendarOrganization = { id: string; descriptor: string };

export type AbsenceCalendarWorker = {
  id: string;
  descriptor: string;
  workerNumber: string;
  photoUrl?: string;
  jobTitle?: string;
  organization?: AbsenceCalendarOrganization;
  entries: AbsenceCalendarEntry[];
};

export type AbsenceCalendarQuery = { from: string; to: string; search: string; limit: number; offset: number; view?: 'team' | 'authorized' | 'all'; organizationId?: string; organizationIds?: string[] };
export type AbsenceCalendarResult = { data: AbsenceCalendarWorker[]; total: number; organizations?: AbsenceCalendarOrganization[] };

const dateKey = (date: Date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

export function plannerPeriod(anchor: string, view: 'month' | 'week') {
  const date = new Date(`${anchor}T00:00:00Z`);
  const start = new Date(date);
  if (view === 'month') start.setUTCDate(1);
  else start.setUTCDate(start.getUTCDate() - start.getUTCDay());
  const end = new Date(start);
  if (view === 'month') end.setUTCMonth(end.getUTCMonth() + 1, 0);
  else end.setUTCDate(end.getUTCDate() + 6);
  const days: { date: string; day: number; weekday: number }[] = [];
  for (const cursor = new Date(start); cursor <= end; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
    days.push({ date: dateKey(cursor), day: cursor.getUTCDate(), weekday: cursor.getUTCDay() });
  }
  return { from: dateKey(start), to: dateKey(end), days };
}

export function movePlannerPeriod(anchor: string, view: 'month' | 'week', offset: number) {
  const date = new Date(`${anchor}T00:00:00Z`);
  if (view === 'month') { date.setUTCDate(1); date.setUTCMonth(date.getUTCMonth() + offset); }
  else date.setUTCDate(date.getUTCDate() + 7 * offset);
  return dateKey(date);
}

export type PlannerSpan = { entry: AbsenceCalendarEntry; start: number; end: number };

// 겹치는 휴가는 별도 줄에 놓고, 화면 경계를 넘는 기간은 현재 표시 구간으로 자른다.
export function plannerLanes(entries: AbsenceCalendarEntry[], days: { date: string }[]): PlannerSpan[][] {
  if (!days.length) return [[]];
  const spans = entries.flatMap((entry) => {
    if (entry.endDate < days[0].date || entry.startDate > days.at(-1)!.date) return [];
    const start = Math.max(0, days.findIndex((day) => day.date >= entry.startDate));
    const end = days.filter((day) => day.date <= entry.endDate).length - 1;
    return end >= start ? [{ entry, start, end }] : [];
  }).sort((a, b) => a.start - b.start || b.end - a.end);
  const lanes: PlannerSpan[][] = [];
  for (const span of spans) {
    const lane = lanes.find((items) => items.at(-1)!.end < span.start);
    if (lane) lane.push(span); else lanes.push([span]);
  }
  return lanes.length ? lanes : [[]];
}
